import { z } from 'zod';

/**
 * Plateforme (CDC §19) : le super-administrateur de la plateforme est distinct des administrateurs d'entreprise.
 * Il crée les entreprises (sans dupliquer le code), choisit leur abonnement, suit l'usage face aux quotas et peut
 * suspendre / réactiver une entreprise. Il ne voit jamais les données métier (dossiers, clients…) des entreprises.
 */
export const QUOTA_CATEGORIES = ['supervisor', 'commercial', 'technician'] as const;
export type QuotaKey = (typeof QUOTA_CATEGORIES)[number];
export const QUOTA_LABELS: Record<QuotaKey, string> = { supervisor: 'Superviseurs', commercial: 'Commerciaux', technician: 'Techniciens' };

export interface PlatformPlan {
  id: string;
  code: string;
  name: string;
  description: string | null;
  monthlyPriceCents: number | null;
  limits: Partial<Record<QuotaKey, number | null>>;
  isActive: boolean;
  tenantCount: number;
}

export interface PlatformTenant {
  id: string;
  slug: string;
  name: string;
  status: 'active' | 'suspended';
  plan: { id: string; code: string; name: string } | null;
  limits: Partial<Record<QuotaKey, number | null>>;
  usage: Record<QuotaKey, number> & { users: number; dossiers: number; storageMb: number };
  admins: { email: string; name: string }[];
  createdAt: string;
}

const email = z.string().trim().toLowerCase().email('E-mail invalide').max(200);
export const platformLoginInput = z.object({ email, password: z.string().min(1).max(200) });

export const tenantCreateInput = z.object({
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9][a-z0-9-]{1,62}$/, 'Identifiant : minuscules, chiffres et tirets'),
  name: z.string().trim().min(2).max(120),
  planCode: z.string().trim().min(2).max(50),
  adminEmail: email,
  adminFirstName: z.string().trim().min(1).max(80),
  adminLastName: z.string().trim().min(1).max(80),
  /** territories copied from the model (GP, MQ…); none = to be created by the company */
  territoryCodes: z.array(z.string().trim().min(1).max(10)).max(20).default([]),
});
export type TenantCreateInput = z.input<typeof tenantCreateInput>;

export const tenantPatchInput = z
  .object({ name: z.string().trim().min(2).max(120), status: z.enum(['active', 'suspended']), planId: z.string().uuid(), reason: z.string().trim().max(500) })
  .partial();

const limit = z.number().int().min(0).max(100_000).nullable();
export const planInput = z.object({
  code: z.string().trim().regex(/^[a-z0-9_]{2,50}$/),
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500).nullish().transform((v) => v || null),
  monthlyPriceCents: z.number().int().min(0).max(100_000_00).nullish().transform((v) => v ?? null),
  limits: z.object({ supervisor: limit, commercial: limit, technician: limit }).partial(),
});
export const planPatchInput = planInput.omit({ code: true }).partial().extend({ isActive: z.boolean().optional() });
export const adminResetInput = z.object({ email });
